import React, { useState } from 'react';
import { Button, Col, Form, Input, message, Row } from 'antd';
import {
  ClockCircleOutlined,
  EnvironmentOutlined,
  LoadingOutlined,
  MailOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import styles from './styles.module.css';

const { TextArea } = Input;

const BUSINESS_EMAIL = 'maiskiiartur@maiskiiassociates.com';
const PHONE = '+1 786-440-3071';

interface FormValues {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  subject: string;
  message: string;
}

function ContactPage() {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const onFinish = async (values: FormValues) => {
    setIsLoading(true);

    // Local dev mock — set REACT_APP_MOCK_CONTACT=true in .env.local to use.
    // Remove or leave false in production; the real /api/contact handles it.
    if (process.env.REACT_APP_MOCK_CONTACT === 'true') {
      await new Promise((r) => setTimeout(r, 800));
      console.log('[mock] Contact form payload:', values);
      message.success(
        'Thank you for your message. We will contact you within 24 hours. A confirmation has been sent to your email.'
      );
      form.resetFields();
      setIsLoading(false);
      return;
    }

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        message.success(
          'Thank you for your message. We will contact you within 24 hours. A confirmation has been sent to your email.'
        );
        form.resetFields();
      } else {
        throw new Error(data.error || 'Submission failed');
      }
    } catch (error: any) {
      console.error('Form submission failed:', error);
      message.error(
        `Failed to send your message. Please email us directly at ${BUSINESS_EMAIL}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.contactContainer}>
      <div className={styles.heroBanner}>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>Contact Us</h1>
          <p className={styles.heroSubtitle}>
            We look forward to helping you navigate your next transaction with
            clarity and confidence.
          </p>
        </div>
      </div>

      <div className={styles.contentSection}>
        <Row gutter={[60, 40]}>
          {/* ── Contact info ── */}
          <Col xs={24} lg={12}>
            <div className={styles.contactInfo}>
              <h2>Get in Touch</h2>

              <div className={styles.infoItem}>
                <div className={styles.infoIcon}>
                  <EnvironmentOutlined />
                </div>
                <div className={styles.infoContent}>
                  <h3>Office Location</h3>
                  <p>
                    USA: Sacramento CA, Miami FL, Seattle WA | IRAQ: Baghdad,
                    Basra, Erbil | JORDAN: Amman | UAE: Dubai | SAUDI ARABIA:
                    Riyadh | LEBANON: Beirut
                  </p>
                  <p className={styles.note}>
                    Serving clients nationwide and internationally
                  </p>
                </div>
              </div>

              <div className={styles.infoItem}>
                <div className={styles.infoIcon}>
                  <MailOutlined />
                </div>
                <div className={styles.infoContent}>
                  <h3>Email</h3>
                  <p>{BUSINESS_EMAIL}</p>
                  <p className={styles.note}>
                    <a
                      href={`mailto:${BUSINESS_EMAIL}`}
                      className={styles.emailLink}
                    >
                      Click to email us directly
                    </a>
                  </p>
                </div>
              </div>

              <div className={styles.infoItem}>
                <div className={styles.infoIcon}>
                  <PhoneOutlined />
                </div>
                <div className={styles.infoContent}>
                  <h3>Phone</h3>
                  <p>{PHONE}</p>
                  <p className={styles.note}>Call or text during business hours</p>
                </div>
              </div>

              <div className={styles.infoItem}>
                <div className={styles.infoIcon}>
                  <ClockCircleOutlined />
                </div>
                <div className={styles.infoContent}>
                  <h3>Business Hours</h3>
                  <p>Monday–Friday</p>
                  <p>9:00 AM – 6:00 PM PST</p>
                  <p className={styles.note}>Consultations by appointment only</p>
                </div>
              </div>

              <div className={styles.responseTime}>
                <h3>Response Time</h3>
                <p>
                  We typically respond within 24 hours during business days. You
                  will receive a confirmation email after submitting this form.
                </p>
              </div>
            </div>
          </Col>

          {/* ── Contact form ── */}
          <Col xs={24} lg={12}>
            <div className={styles.contactForm}>
              <h2>Send Us a Message</h2>

              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                className={styles.form}
              >
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="name"
                      rules={[{ required: true, message: 'Please enter your full name' }]}
                    >
                      <Input placeholder="Full Name" size="large" disabled={isLoading} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="email"
                      rules={[
                        { required: true, message: 'Please enter your email address' },
                        { type: 'email', message: 'Please enter a valid email address' },
                      ]}
                    >
                      <Input placeholder="Email Address" size="large" disabled={isLoading} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="phone"
                      rules={[
                        {
                          pattern: /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
                          message: 'Please enter a valid phone number',
                        },
                      ]}
                    >
                      <Input
                        placeholder="Phone Number (Optional)"
                        size="large"
                        disabled={isLoading}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="company">
                      <Input
                        placeholder="Company (Optional)"
                        size="large"
                        disabled={isLoading}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="subject"
                  rules={[{ required: true, message: 'Please enter a subject' }]}
                >
                  <Input placeholder="Subject" size="large" disabled={isLoading} />
                </Form.Item>

                <Form.Item
                  name="message"
                  rules={[
                    { required: true, message: 'Please enter your message' },
                    { min: 10, message: 'Please provide more detail (minimum 10 characters)' },
                  ]}
                >
                  <TextArea
                    placeholder="Your Message — please include details about your legal needs"
                    rows={6}
                    size="large"
                    disabled={isLoading}
                    showCount
                    maxLength={2000}
                  />
                </Form.Item>

                <Form.Item>
                  <div className={styles.disclaimer}>
                    <p>
                      <strong>Important Disclaimer:</strong> By submitting this
                      form, you acknowledge that:
                    </p>
                    <ul>
                      <li>No attorney-client relationship is created</li>
                      <li>Do not include confidential or sensitive information</li>
                      <li>We will respond to discuss potential representation</li>
                      <li>
                        Information provided is for initial consultation purposes only
                      </li>
                    </ul>
                  </div>
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    block
                    className={styles.submitButton}
                    loading={isLoading}
                    icon={isLoading ? <LoadingOutlined /> : null}
                  >
                    {isLoading ? 'Sending…' : 'Send Message'}
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default ContactPage;















// import React, { useState } from 'react';
// import { Button, Col, Form, Input, message, Row } from 'antd';
// import {
//   ClockCircleOutlined,
//   EnvironmentOutlined,
//   LoadingOutlined,
//   MailOutlined,
//   PhoneOutlined,
// } from '@ant-design/icons';
// import { useNavigate } from 'react-router-dom';
// import styles from './styles.module.css';
//
// const { TextArea } = Input;
//
// const BUSINESS_EMAIL = 'maiskiiartur@maiskiiassociates.com';
// const PHONE = '+1 786-440-3071';
//
// interface FormValues {
//   name: string;
//   email: string;
//   phone?: string;
//   company?: string;
//   subject: string;
//   message: string;
// }
//
// function ContactPage() {
//   const [form] = Form.useForm();
//   const [isLoading, setIsLoading] = useState(false);
//   const navigate = useNavigate();
//
//   const onFinish = async (values: FormValues) => {
//     setIsLoading(true);
//
//     try {
//       // AbortController gives us a hard timeout so the button never
//       // spins forever if the network or Web3Forms hangs.
//       const controller = new AbortController();
//       const timeout = setTimeout(() => controller.abort(), 30000); // 10s max
//
//       const res = await fetch('https://api.web3forms.com/submit', {
//         method: 'POST',
//         signal: controller.signal,
//         headers: {
//           'Content-Type': 'application/json',
//           Accept: 'application/json',
//         },
//         body: JSON.stringify({
//           access_key: process.env.REACT_APP_WEB3FORMS_KEY,
//           subject: `New inquiry: ${values.subject}`,
//           from_name: values.name,
//           email: values.email,
//           phone: values.phone || 'Not provided',
//           company: values.company || 'Not provided',
//           message: values.message,
//         }),
//       });
//
//       clearTimeout(timeout);
//       const data = await res.json();
//
//       if (res.ok && data.success) {
//         message.success(
//           'Thank you for your message. We will contact you within 24 hours.'
//         );
//         form.resetFields();
//       } else {
//         throw new Error(data.message || `Server error ${res.status}`);
//       }
//     } catch (error: any) {
//       if (error.name === 'AbortError') {
//         message.error('Request timed out. Please try again or email us directly.');
//       } else {
//         console.error('Form submission failed:', error);
//         message.error(
//           `Failed to send your message. Please email us directly at ${BUSINESS_EMAIL}`
//         );
//       }
//     } finally {
//       setIsLoading(false);
//     }
//   };
//
//   return (
//     <div className={styles.contactContainer}>
//       <div className={styles.heroBanner}>
//         <div className={styles.heroContent}>
//           <h1 className={styles.heroTitle}>Contact Us</h1>
//           <p className={styles.heroSubtitle}>
//             We look forward to helping you navigate your next transaction with
//             clarity and confidence.
//           </p>
//         </div>
//       </div>
//
//       <div className={styles.contentSection}>
//         <Row gutter={[60, 40]}>
//           {/* ── Contact info ── */}
//           <Col xs={24} lg={12}>
//             <div className={styles.contactInfo}>
//               <h2>Get in Touch</h2>
//
//               <div className={styles.infoItem}>
//                 <div className={styles.infoIcon}>
//                   <EnvironmentOutlined />
//                 </div>
//                 <div className={styles.infoContent}>
//                   <h3>Office Location</h3>
//                   <p>
//                     USA: Sacramento CA, Miami FL, Seattle WA | IRAQ: Baghdad,
//                     Basra, Erbil | JORDAN: Amman | UAE: Dubai | SAUDI ARABIA:
//                     Riyadh | LEBANON: Beirut
//                   </p>
//                   <p className={styles.note}>
//                     Serving clients nationwide and internationally
//                   </p>
//                 </div>
//               </div>
//
//               <div className={styles.infoItem}>
//                 <div className={styles.infoIcon}>
//                   <MailOutlined />
//                 </div>
//                 <div className={styles.infoContent}>
//                   <h3>Email</h3>
//                   <p>{BUSINESS_EMAIL}</p>
//                   <p className={styles.note}>
//                     <a
//                       href={`mailto:${BUSINESS_EMAIL}`}
//                       className={styles.emailLink}
//                     >
//                       Click to email us directly
//                     </a>
//                   </p>
//                 </div>
//               </div>
//
//               <div className={styles.infoItem}>
//                 <div className={styles.infoIcon}>
//                   <PhoneOutlined />
//                 </div>
//                 <div className={styles.infoContent}>
//                   <h3>Phone</h3>
//                   <p>{PHONE}</p>
//                   <p className={styles.note}>Call or text during business hours</p>
//                 </div>
//               </div>
//
//               <div className={styles.infoItem}>
//                 <div className={styles.infoIcon}>
//                   <ClockCircleOutlined />
//                 </div>
//                 <div className={styles.infoContent}>
//                   <h3>Business Hours</h3>
//                   <p>Monday–Friday</p>
//                   <p>9:00 AM – 6:00 PM PST</p>
//                   <p className={styles.note}>Consultations by appointment only</p>
//                 </div>
//               </div>
//
//               <div className={styles.responseTime}>
//                 <h3>Response Time</h3>
//                 <p>
//                   We typically respond within 24 hours during business days. You
//                   will receive a confirmation email after submitting this form.
//                 </p>
//               </div>
//             </div>
//           </Col>
//
//           {/* ── Contact form ── */}
//           <Col xs={24} lg={12}>
//             <div className={styles.contactForm}>
//               <h2>Send Us a Message</h2>
//
//               <Form
//                 form={form}
//                 layout="vertical"
//                 onFinish={onFinish}
//                 className={styles.form}
//               >
//                 <Row gutter={16}>
//                   <Col xs={24} md={12}>
//                     <Form.Item
//                       name="name"
//                       rules={[{ required: true, message: 'Please enter your full name' }]}
//                     >
//                       <Input placeholder="Full Name" size="large" disabled={isLoading} />
//                     </Form.Item>
//                   </Col>
//                   <Col xs={24} md={12}>
//                     <Form.Item
//                       name="email"
//                       rules={[
//                         { required: true, message: 'Please enter your email address' },
//                         { type: 'email', message: 'Please enter a valid email address' },
//                       ]}
//                     >
//                       <Input placeholder="Email Address" size="large" disabled={isLoading} />
//                     </Form.Item>
//                   </Col>
//                 </Row>
//
//                 <Row gutter={16}>
//                   <Col xs={24} md={12}>
//                     <Form.Item
//                       name="phone"
//                       rules={[
//                         {
//                           pattern: /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
//                           message: 'Please enter a valid phone number',
//                         },
//                       ]}
//                     >
//                       <Input
//                         placeholder="Phone Number (Optional)"
//                         size="large"
//                         disabled={isLoading}
//                       />
//                     </Form.Item>
//                   </Col>
//                   <Col xs={24} md={12}>
//                     <Form.Item name="company">
//                       <Input
//                         placeholder="Company (Optional)"
//                         size="large"
//                         disabled={isLoading}
//                       />
//                     </Form.Item>
//                   </Col>
//                 </Row>
//
//                 <Form.Item
//                   name="subject"
//                   rules={[{ required: true, message: 'Please enter a subject' }]}
//                 >
//                   <Input placeholder="Subject" size="large" disabled={isLoading} />
//                 </Form.Item>
//
//                 <Form.Item
//                   name="message"
//                   rules={[
//                     { required: true, message: 'Please enter your message' },
//                     { min: 10, message: 'Please provide more detail (minimum 10 characters)' },
//                   ]}
//                 >
//                   <TextArea
//                     placeholder="Your Message — please include details about your legal needs"
//                     rows={6}
//                     size="large"
//                     disabled={isLoading}
//                     showCount
//                     maxLength={2000}
//                   />
//                 </Form.Item>
//
//                 <Form.Item>
//                   <div className={styles.disclaimer}>
//                     <p>
//                       <strong>Important Disclaimer:</strong> By submitting this
//                       form, you acknowledge that:
//                     </p>
//                     <ul>
//                       <li>No attorney-client relationship is created</li>
//                       <li>Do not include confidential or sensitive information</li>
//                       <li>We will respond to discuss potential representation</li>
//                       <li>
//                         Information provided is for initial consultation purposes
//                         only
//                       </li>
//                     </ul>
//                   </div>
//                 </Form.Item>
//
//                 <Form.Item style={{ marginBottom: 0 }}>
//                   <Button
//                     type="primary"
//                     htmlType="submit"
//                     size="large"
//                     block
//                     className={styles.submitButton}
//                     loading={isLoading}
//                     icon={isLoading ? <LoadingOutlined /> : null}
//                   >
//                     {isLoading ? 'Sending…' : 'Send Message'}
//                   </Button>
//                 </Form.Item>
//               </Form>
//             </div>
//           </Col>
//         </Row>
//       </div>
//     </div>
//   );
// }
//
// export default ContactPage;













// import React, { useState } from 'react';
// import { Button, Col, Form, Input, message, Row } from 'antd';
// import {
//   ClockCircleOutlined,
//   EnvironmentOutlined,
//   LoadingOutlined,
//   MailOutlined,
//   PhoneOutlined,
// } from '@ant-design/icons';
// import { useNavigate } from 'react-router-dom';
// import styles from './styles.module.css';
//
// const { TextArea } = Input;
//
// // Web3Forms — free, no backend needed, 250 submissions/month.
// // Get your access key at https://web3forms.com (takes 30 seconds, just enter your email).
// // Then add to your .env:  REACT_APP_WEB3FORMS_KEY=your_key_here
// const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY || '';
//
// const BUSINESS_EMAIL = 'maiskiiartur@maiskiiassociates.com';
// const PHONE = '+1 786-440-3071';
//
// function ContactPage() {
//   const [form] = Form.useForm();
//   const [isLoading, setIsLoading] = useState(false);
//   const navigate = useNavigate();
//
//   const onFinish = async (values: {
//     name: string;
//     email: string;
//     phone?: string;
//     company?: string;
//     subject: string;
//     message: string;
//   }) => {
//     // If no key configured, open native mailto as fallback
//     if (!WEB3FORMS_KEY) {
//       const body = encodeURIComponent(
//         `Name: ${values.name}\nEmail: ${values.email}\nPhone: ${values.phone || 'N/A'}\nCompany: ${values.company || 'N/A'}\n\n${values.message}`
//       );
//       window.location.href = `mailto:${BUSINESS_EMAIL}?subject=${encodeURIComponent(values.subject)}&body=${body}`;
//       return;
//     }
//
//     setIsLoading(true);
//
//     try {
//       const payload = {
//         access_key: WEB3FORMS_KEY,
//         // Web3Forms sends this to the email address tied to your access key
//         subject: `New inquiry: ${values.subject}`,
//         from_name: values.name,
//         email: values.email, // reply-to
//         phone: values.phone || 'Not provided',
//         company: values.company || 'Not provided',
//         message: values.message,
//         // Sends an automatic confirmation copy to the client
//         botcheck: '',
//       };
//
//       const res = await fetch('https://api.web3forms.com/submit', {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
//         body: JSON.stringify(payload),
//       });
//
//       const data = await res.json();
//
//       if (data.success) {
//         message.success(
//           'Thank you for your message. We will contact you within 24 hours.'
//         );
//         form.resetFields();
//       } else {
//         throw new Error(data.message || 'Submission failed');
//       }
//     } catch (error: any) {
//       console.error('Form submission failed:', error);
//       message.error(
//         `Failed to send message. Please email us directly at ${BUSINESS_EMAIL}`
//       );
//     } finally {
//       setIsLoading(false);
//     }
//   };
//
//   return (
//     <div className={styles.contactContainer}>
//       <div className={styles.heroBanner}>
//         <div className={styles.heroContent}>
//           <h1 className={styles.heroTitle}>Contact Us</h1>
//           <p className={styles.heroSubtitle}>
//             We look forward to helping you navigate your next transaction with
//             clarity and confidence.
//           </p>
//         </div>
//       </div>
//
//       <div className={styles.contentSection}>
//         <Row gutter={[60, 40]}>
//           {/* ── Contact info ── */}
//           <Col xs={24} lg={12}>
//             <div className={styles.contactInfo}>
//               <h2>Get in Touch</h2>
//
//               <div className={styles.infoItem}>
//                 <div className={styles.infoIcon}>
//                   <EnvironmentOutlined />
//                 </div>
//                 <div className={styles.infoContent}>
//                   <h3>Office Location</h3>
//                   <p>
//                     USA: Sacramento CA, Miami FL, Seattle WA | IRAQ: Baghdad,
//                     Basra, Erbil | JORDAN: Amman | UAE: Dubai | SAUDI ARABIA:
//                     Riyadh | LEBANON: Beirut
//                   </p>
//                   <p className={styles.note}>
//                     Serving clients nationwide and internationally
//                   </p>
//                 </div>
//               </div>
//
//               <div className={styles.infoItem}>
//                 <div className={styles.infoIcon}>
//                   <MailOutlined />
//                 </div>
//                 <div className={styles.infoContent}>
//                   <h3>Email</h3>
//                   <p>{BUSINESS_EMAIL}</p>
//                   <p className={styles.note}>
//                     <a
//                       href={`mailto:${BUSINESS_EMAIL}`}
//                       className={styles.emailLink}
//                     >
//                       Click to email us directly
//                     </a>
//                   </p>
//                 </div>
//               </div>
//
//               <div className={styles.infoItem}>
//                 <div className={styles.infoIcon}>
//                   <PhoneOutlined />
//                 </div>
//                 <div className={styles.infoContent}>
//                   <h3>Phone</h3>
//                   <p>{PHONE}</p>
//                   <p className={styles.note}>Call or text during business hours</p>
//                 </div>
//               </div>
//
//               <div className={styles.infoItem}>
//                 <div className={styles.infoIcon}>
//                   <ClockCircleOutlined />
//                 </div>
//                 <div className={styles.infoContent}>
//                   <h3>Business Hours</h3>
//                   <p>Monday–Friday</p>
//                   <p>9:00 AM – 6:00 PM PST</p>
//                   <p className={styles.note}>Consultations by appointment only</p>
//                 </div>
//               </div>
//
//               <div className={styles.responseTime}>
//                 <h3>Response Time</h3>
//                 <p>
//                   We typically respond within 24 hours during business days. You
//                   will receive a confirmation email after submitting this form.
//                 </p>
//               </div>
//             </div>
//           </Col>
//
//           {/* ── Contact form ── */}
//           <Col xs={24} lg={12}>
//             <div className={styles.contactForm}>
//               <h2>Send Us a Message</h2>
//
//               {!WEB3FORMS_KEY && (
//                 <div className={styles.configWarning}>
//                   <p>
//                     ⚠️ Email service not configured — submitting will open your
//                     mail client instead. Add{' '}
//                     <code>REACT_APP_WEB3FORMS_KEY</code> to your .env to enable
//                     the web form.
//                   </p>
//                 </div>
//               )}
//
//               <Form
//                 form={form}
//                 layout="vertical"
//                 onFinish={onFinish}
//                 className={styles.form}
//               >
//                 <Row gutter={16}>
//                   <Col xs={24} md={12}>
//                     <Form.Item
//                       name="name"
//                       rules={[{ required: true, message: 'Please enter your full name' }]}
//                     >
//                       <Input placeholder="Full Name" size="large" disabled={isLoading} />
//                     </Form.Item>
//                   </Col>
//                   <Col xs={24} md={12}>
//                     <Form.Item
//                       name="email"
//                       rules={[
//                         { required: true, message: 'Please enter your email address' },
//                         { type: 'email', message: 'Please enter a valid email address' },
//                       ]}
//                     >
//                       <Input placeholder="Email Address" size="large" disabled={isLoading} />
//                     </Form.Item>
//                   </Col>
//                 </Row>
//
//                 <Row gutter={16}>
//                   <Col xs={24} md={12}>
//                     <Form.Item
//                       name="phone"
//                       rules={[
//                         {
//                           pattern: /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
//                           message: 'Please enter a valid phone number',
//                         },
//                       ]}
//                     >
//                       <Input
//                         placeholder="Phone Number (Optional)"
//                         size="large"
//                         disabled={isLoading}
//                       />
//                     </Form.Item>
//                   </Col>
//                   <Col xs={24} md={12}>
//                     <Form.Item name="company">
//                       <Input
//                         placeholder="Company (Optional)"
//                         size="large"
//                         disabled={isLoading}
//                       />
//                     </Form.Item>
//                   </Col>
//                 </Row>
//
//                 <Form.Item
//                   name="subject"
//                   rules={[{ required: true, message: 'Please enter a subject' }]}
//                 >
//                   <Input placeholder="Subject" size="large" disabled={isLoading} />
//                 </Form.Item>
//
//                 <Form.Item
//                   name="message"
//                   rules={[
//                     { required: true, message: 'Please enter your message' },
//                     { min: 10, message: 'Please provide more detail (minimum 10 characters)' },
//                   ]}
//                 >
//                   <TextArea
//                     placeholder="Your Message — please include details about your legal needs"
//                     rows={6}
//                     size="large"
//                     disabled={isLoading}
//                     showCount
//                     maxLength={2000}
//                   />
//                 </Form.Item>
//
//                 <Form.Item>
//                   <div className={styles.disclaimer}>
//                     <p>
//                       <strong>Important Disclaimer:</strong> By submitting this
//                       form, you acknowledge that:
//                     </p>
//                     <ul>
//                       <li>No attorney-client relationship is created</li>
//                       <li>Do not include confidential or sensitive information</li>
//                       <li>We will respond to discuss potential representation</li>
//                       <li>
//                         Information provided is for initial consultation purposes
//                         only
//                       </li>
//                     </ul>
//                   </div>
//                 </Form.Item>
//
//                 <Form.Item style={{ marginBottom: 0 }}>
//                   <Button
//                     type="primary"
//                     htmlType="submit"
//                     size="large"
//                     block
//                     className={styles.submitButton}
//                     loading={isLoading}
//                     icon={isLoading ? <LoadingOutlined /> : null}
//                   >
//                     {isLoading ? 'Sending…' : 'Send Message'}
//                   </Button>
//                 </Form.Item>
//               </Form>
//             </div>
//           </Col>
//         </Row>
//       </div>
//     </div>
//   );
// }
//
// export default ContactPage;
